import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Shuffle, 
  Repeat, 
  Repeat1,
  List,
  Volume2, 
  VolumeX,
  Heart,
  Music,
  ListMusic
} from 'lucide-react';
import dataManager from '../utils/dataManager';

const MusicPlayer = ({ 
  currentSong, 
  songInfo, 
  isPlaying, 
  onPlayPause, 
  onOpenPlayer,
  playMode,
  onPlayModeChange,
  onNextSong,
  onPrevSong,
  onSongEnd,
  hasNext,
  hasPrev
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(80);
  const [isLiked, setIsLiked] = useState(false);
  const audioRef = useRef(null);

  // 初始化音量设置
  useEffect(() => {
    const savedVolume = localStorage.getItem('tunefree_volume');
    const savedMuted = localStorage.getItem('tunefree_muted');
    
    if (savedVolume) {
      setVolume(Number(savedVolume));
    }
    if (savedMuted === 'true') {
      setIsMuted(true);
    }
  }, []);

  // 检查收藏状态
  useEffect(() => {
    if (currentSong) {
      setIsLiked(dataManager.isFavorite(currentSong.id, currentSong.platform));
    }
  }, [currentSong]);

  // 处理音频加载
  useEffect(() => {
    if (songInfo && audioRef.current && songInfo.url) {
      const audio = audioRef.current;
      
      // 避免重复加载相同的音频
      if (audio.src !== songInfo.url) {
        audio.src = songInfo.url;
        audio.load();
      }
    }
  }, [songInfo]);

  // 处理播放/暂停状态
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !songInfo?.url) return;

    const handlePlay = async () => {
      try {
        if (isPlaying) {
          // 确保音频已加载完成再播放
          if (audio.readyState >= 2) { // HAVE_CURRENT_DATA 或更高
            await audio.play();
          } else {
            // 等待音频加载完成后再播放
            const handleCanPlay = async () => {
              try {
                await audio.play();
              } catch (error) {
                console.error('播放失败:', error);
              }
              audio.removeEventListener('canplay', handleCanPlay);
            };
            audio.addEventListener('canplay', handleCanPlay);
          }
        } else {
          audio.pause();
        }
      } catch (error) {
        console.error('音频播放控制失败:', error);
      }
    };

          handlePlay();
    }, [isPlaying, songInfo]);

  // 处理音量变化
  const handleVolumeChange = useCallback((newVolume) => {
    setVolume(newVolume);
    localStorage.setItem('tunefree_volume', newVolume.toString());
    
    // 如果之前是静音状态，取消静音
    if (isMuted && newVolume > 0) {
      setIsMuted(false);
      localStorage.setItem('tunefree_muted', 'false');
    }
  }, [isMuted]);

  // 切换静音状态
  const toggleMute = useCallback(() => {
    if (isMuted) {
      // 取消静音，恢复之前的音量
      setIsMuted(false);
      setVolume(previousVolume);
      localStorage.setItem('tunefree_muted', 'false');
      localStorage.setItem('tunefree_volume', previousVolume.toString());
    } else {
      // 静音，保存当前音量
      setPreviousVolume(volume);
      setIsMuted(true);
      localStorage.setItem('tunefree_muted', 'true');
    }
  }, [isMuted, volume, previousVolume]);

  // 同步音量到audio元素
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100;
      audioRef.current.muted = isMuted;
    }
  }, [volume, isMuted]);

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyPress = (e) => {
      // 只在没有输入框获得焦点时响应快捷键
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch (e.key.toLowerCase()) {
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        case 'arrowup':
          e.preventDefault();
          handleVolumeChange(Math.min(100, volume + 5));
          break;
        case 'arrowdown':
          e.preventDefault();
          handleVolumeChange(Math.max(0, volume - 5));
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [volume, toggleMute, handleVolumeChange]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleCanPlay = () => {
    // 音频可以播放时，如果需要播放则开始播放
    if (isPlaying && audioRef.current) {
      audioRef.current.play().catch(error => {
        console.error('自动播放失败:', error);
      });
    }
  };

  const handleError = (error) => {
    console.error('音频加载失败:', error);
  };

  const handleSeek = (e) => {
    const newTime = Number(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const toggleLike = () => {
    if (!currentSong) return;
    
    if (isLiked) {
      dataManager.removeFromFavorites(currentSong.id, currentSong.platform);
    } else {
      dataManager.addToFavorites(currentSong);
    }
    setIsLiked(!isLiked);
  };

  // 播放模式图标和文本
  const getPlayModeInfo = (mode) => {
    switch (mode) {
      case 'shuffle':
        return { icon: Shuffle, text: '随机播放', color: 'text-green-500' };
      case 'repeat':
        return { icon: Repeat, text: '列表循环', color: 'text-blue-500' };
      case 'single':
        return { icon: Repeat1, text: '单曲循环', color: 'text-purple-500' };
      default:
        return { icon: List, text: '顺序播放', color: 'text-dark-400' };
    }
  };

  // 切换播放模式
  const handlePlayModeToggle = () => {
    if (!onPlayModeChange) return;
    
    const modes = ['list', 'repeat', 'shuffle', 'single'];
    const currentIndex = modes.indexOf(playMode);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    onPlayModeChange(nextMode);
  };




  if (!currentSong) {
    return (
      <div className="h-20 bg-dark-100 border-t border-dark-200 flex items-center justify-center">
        <p className="text-dark-500">暂无播放内容</p>
      </div>
    );
  }

  return (
    <div className="h-20 bg-dark-100 border-t border-dark-200 flex items-center px-4">
      {/* 音频元素 */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onError={handleError}
        onEnded={() => onSongEnd && onSongEnd()}
        preload="metadata"
      />

      {/* 当前播放信息 */}
      <div className="flex items-center gap-3 w-1/4 min-w-0">
        <div 
          className="w-12 h-12 bg-dark-200 rounded-lg flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-dark-300 transition-colors duration-200"
          onClick={() => onOpenPlayer('player')}
        >
          {songInfo?.pic ? (
            <img
              src={songInfo.pic}
              alt={currentSong.name}
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <Music size={20} className="text-dark-400" />
        </div>
        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onOpenPlayer('player')}>
          <h4 className="text-sm font-medium text-dark-700 truncate">
            {songInfo?.name || currentSong.name}
          </h4>
          <p className="text-xs text-dark-400 truncate">
            {songInfo?.artist || currentSong.artist}
          </p>
        </div>
        <button
          onClick={toggleLike}
          className="p-1 hover:scale-110 transition-transform duration-200"
        >
          <Heart 
            size={16} 
            className={isLiked ? 'text-red-500 fill-current' : 'text-dark-400'} 
          />
        </button>
        
      </div>

      {/* 播放控制 */}
      <div className="flex-1 max-w-2xl mx-8">
        {/* 控制按钮 */}
        <div className="flex items-center justify-center gap-4 mb-2">
          <button 
            onClick={handlePlayModeToggle}
            className="p-1 hover:scale-110 transition-transform duration-200"
            title={getPlayModeInfo(playMode).text}
          >
            {React.createElement(getPlayModeInfo(playMode).icon, {
              size: 16,
              className: `${getPlayModeInfo(playMode).color} hover:brightness-110`
            })}
          </button>
          <button 
            onClick={onPrevSong}
            disabled={!hasPrev}
            className="p-2 hover:scale-110 transition-transform duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SkipBack size={20} className="text-dark-600" />
          </button>
          <button
            onClick={onPlayPause}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-transform duration-200 shadow-lg"
          >
            {isPlaying ? (
              <Pause size={20} className="text-dark-50" />
            ) : (
              <Play size={20} className="text-dark-50 ml-0.5" />
            )}
          </button>
          <button 
            onClick={onNextSong}
            disabled={!hasNext}
            className="p-2 hover:scale-110 transition-transform duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SkipForward size={20} className="text-dark-600" />
          </button>
          <button 
            onClick={() => onOpenPlayer('queue')}
            className="p-1 hover:scale-110 transition-transform duration-200"
            title="播放队列"
          >
            <ListMusic size={16} className="text-dark-400 hover:text-primary-400" />
          </button>
        </div>

        {/* 进度条 */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-dark-400 w-10 text-right">
            {formatTime(currentTime)}
          </span>
          <div className="flex-1 relative">
            <div className="h-1 bg-dark-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary-400 transition-all duration-100"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <input
              type="range"
              min="0"
              max={duration}
              value={currentTime}
              onChange={handleSeek}
              className="absolute inset-0 w-full h-1 opacity-0 cursor-pointer"
            />
          </div>
          <span className="text-xs text-dark-400 w-10">
            {formatTime(duration)}
          </span>
        </div>
      </div>

      {/* 音量控制 */}
      <div className="flex items-center gap-2 w-1/4 justify-end">
        <button 
          onClick={toggleMute}
          className="p-1 hover:scale-110 transition-transform duration-200"
          title={isMuted ? '取消静音' : '静音'}
        >
          {isMuted ? (
            <VolumeX size={16} className="text-red-500" />
          ) : (
            <Volume2 size={16} className="text-dark-400 hover:text-primary-400" />
          )}
        </button>
        <div className="w-20 relative">
          <div className="h-1 bg-dark-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-colors duration-200 ${
                isMuted ? 'bg-gray-400' : 'bg-primary-400'
              }`}
              style={{ width: `${isMuted ? 0 : volume}%` }}
            />
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={(e) => handleVolumeChange(Number(e.target.value))}
            className="absolute inset-0 w-full h-1 opacity-0 cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
};

export default MusicPlayer;
